'use client';

import React, { useState } from 'react';
import Icon from '@/components/ui/AppIcon';

interface PlantOption {
  id: string;
  name: string;
  variety: string;
  difficulty: 'Easy' | 'Medium' | 'Hard';
  expectedYield: string;
  growthTime: string;
}

interface CustomizationPanelProps {
  currentPlants: PlantOption[];
  availableAlternatives: PlantOption[];
  onPlantChange: (oldPlantId: string, newPlantId: string) => void;
  onRecalculate: () => void;
}

const CustomizationPanel = ({ 
  currentPlants, 
  availableAlternatives, 
  onPlantChange, 
  onRecalculate 
}: CustomizationPanelProps) => {
  const [selectedPlant, setSelectedPlant] = useState<string | null>(null);
  const [showAlternatives, setShowAlternatives] = useState(false);

  const getDifficultyColor = (difficulty: string) => {
    switch (difficulty) {
      case 'Easy': return 'text-success bg-success/10';
      case 'Medium': return 'text-warning bg-warning/10';
      case 'Hard': return 'text-error bg-error/10';
      default: return 'text-muted-foreground bg-muted';
    }
  };

  const handlePlantSelect = (plantId: string) => {
    setSelectedPlant(plantId);
    setShowAlternatives(true);
  };

  const handlePlantChange = (newPlantId: string) => {
    if (selectedPlant) {
      onPlantChange(selectedPlant, newPlantId);
      setSelectedPlant(null);
      setShowAlternatives(false);
    }
  };

  return (
    <div className="bg-card rounded-lg border border-border p-6">
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-lg font-semibold text-foreground">Customize Your Plan</h3>
        <button
          onClick={onRecalculate}
          className="flex items-center space-x-2 px-3 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors duration-200"
        >
          <Icon name="ArrowPathIcon" size={16} />
          <span className="text-sm font-medium">Recalculate</span>
        </button>
      </div>

      {/* Current Plants */}
      <div className="mb-6">
        <h4 className="text-md font-semibold text-foreground mb-4">Current Plant Selection</h4>
        <div className="space-y-3">
          {currentPlants.map((plant) => (
            <div
              key={plant.id}
              className={`p-3 border rounded-lg cursor-pointer transition-colors duration-200 ${
                selectedPlant === plant.id
                  ? 'border-primary bg-primary/5' :'border-border hover:bg-muted/50'
              }`}
              onClick={() => handlePlantSelect(plant.id)}
            >
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <div className="flex items-center space-x-3 mb-2">
                    <h5 className="font-medium text-foreground">{plant.name}</h5>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${getDifficultyColor(plant.difficulty)}`}>
                      {plant.difficulty}
                    </span>
                  </div>
                  <p className="text-sm text-muted-foreground mb-2">{plant.variety}</p>
                  <div className="flex items-center space-x-4 text-xs text-muted-foreground">
                    <span>Yield: {plant.expectedYield}</span>
                    <span>Growth: {plant.growthTime}</span>
                  </div>
                </div>
                <Icon name="ChevronRightIcon" size={20} className="text-muted-foreground" />
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Alternative Options */}
      {showAlternatives && (
        <div className="border-t border-border pt-6">
          <div className="flex items-center justify-between mb-4">
            <h4 className="text-md font-semibold text-foreground">Alternative Options</h4>
            <button
              onClick={() => {
                setShowAlternatives(false);
                setSelectedPlant(null);
              }}
              className="text-muted-foreground hover:text-foreground"
            >
              <Icon name="XMarkIcon" size={20} />
            </button>
          </div>
          
          <div className="space-y-3">
            {availableAlternatives.map((plant) => (
              <div
                key={plant.id}
                className="p-3 border border-border rounded-lg cursor-pointer hover:bg-muted/50 transition-colors duration-200"
                onClick={() => handlePlantChange(plant.id)}
              >
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-3 mb-2">
                      <h5 className="font-medium text-foreground">{plant.name}</h5>
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${getDifficultyColor(plant.difficulty)}`}>
                        {plant.difficulty}
                      </span>
                    </div>
                    <p className="text-sm text-muted-foreground mb-2">{plant.variety}</p>
                    <div className="flex items-center space-x-4 text-xs text-muted-foreground">
                      <span>Yield: {plant.expectedYield}</span>
                      <span>Growth: {plant.growthTime}</span>
                    </div>
                  </div>
                  <Icon name="PlusCircleIcon" size={20} className="text-primary" />
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Customization Tips */}
      <div className="mt-6 p-4 bg-surface rounded-lg border border-border">
        <div className="flex items-center space-x-2 mb-3">
          <Icon name="LightBulbIcon" size={20} className="text-accent" />
          <h4 className="font-semibold text-foreground">Customization Tips</h4>
        </div>
        <ul className="text-sm text-muted-foreground space-y-2">
          <li className="flex items-start space-x-2">
            <Icon name="CheckIcon" size={14} className="text-success mt-0.5 flex-shrink-0" />
            <span>Click on any plant to see alternative options</span>
          </li>
          <li className="flex items-start space-x-2">
            <Icon name="CheckIcon" size={14} className="text-success mt-0.5 flex-shrink-0" />
            <span>Consider your experience level when choosing difficulty</span>
          </li>
          <li className="flex items-start space-x-2">
            <Icon name="CheckIcon" size={14} className="text-success mt-0.5 flex-shrink-0" />
            <span>Recalculate after changes to update projections</span>
          </li>
        </ul>
      </div>
    </div>
  );
};

export default CustomizationPanel;